import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "@emotion/react/jsx-runtime";
/* eslint-disable unicorn/prefer-logical-operator-over-ternary */
/** @jsxImportSource @emotion/react */
import { useRef, useCallback, memo, forwardRef, useState, useEffect, } from 'react';
import { useTable, useSortBy } from 'react-table';
import { useMeasure } from 'react-use';
import checkModifierKeyActivated from '../../../data/utilities/checkModifierKeyActivated';
import ContextMenu from '../ContextMenu';
import ReactTableHeader from './Elements/ReactTableHeader';
import ReactTableRow from './Elements/ReactTableRow';
import { ReactTableStyle } from './Style';
import { ReactTableProvider, useReactTableContext, } from './utility/ReactTableContext';
import useRowSpan, { prepareRowSpan } from './utility/useRowSpan';
const styles = {
    table: (enableVirtualScroll) => {
        if (enableVirtualScroll) {
            return { position: 'sticky', top: 0 };
        }
    },
};
const counterStyle = {
    position: 'absolute',
    bottom: 0,
    width: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    pointerEvents: 'none',
    textAlign: 'center',
    fontWeight: 'bolder',
    color: 'white',
    fontSize: '1.4em',
};
const ReactTableInner = forwardRef(function ReactTableInner(props, ref) {
    const { data, columns, highlightedSource, context = null, onScroll, approxItemHeight = 40, enableVirtualScroll = false, groupKey, onClick, highlightActiveRow = false, totalCount, indexKey = 'index', } = props;
    const contextRef = useRef(null);
    const { index: indexBoundary } = useReactTableContext();
    const [rowIndex, setRowIndex] = useState();
    const timeoutIdRef = useRef();
    const [isCounterVisible, setCounterVisibility] = useState(false);
    const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow, rowSpanHeaders, } = useTable({
        columns,
        data,
    }, useSortBy, useRowSpan);
    const contextMenuHandler = useCallback((e, row) => {
        if (!checkModifierKeyActivated(e)) {
            e.preventDefault();
            contextRef.current.handleContextMenu(e, row.original);
        }
    }, [contextRef]);
    const rowsData = enableVirtualScroll
        ? rows.slice(indexBoundary.start, indexBoundary.end)
        : rows;
    const clickHandler = useCallback((event, row) => {
        setRowIndex(row.index);
        onClick?.(event, row);
    }, [onClick]);
    function scrollHandler(e) {
        if (enableVirtualScroll) {
            onScroll(e);
        }
        if (timeoutIdRef.current) {
            clearTimeout(timeoutIdRef.current);
            setCounterVisibility(true);
        }
        timeoutIdRef.current = setTimeout(() => {
            setCounterVisibility(false);
        }, 1000);
    }
    const index = rowsData[rowsData.length - 1]?.original[indexKey] ||
        rowsData[rowsData.length - 1]?.index;
    const total = totalCount ? totalCount : data.length;
    return (_jsxs(_Fragment, { children: [_jsxs("div", { ref: ref, className: "table-container", style: {
                    overflowY: 'auto',
                    position: 'relative',
                    height: '100%',
                }, onScroll: scrollHandler, children: [enableVirtualScroll && (_jsx("div", { style: {
                            height: approxItemHeight * data.length,
                            position: 'absolute',
                            width: '100%',
                            pointerEvents: 'none',
                        } })), _jsxs("table", { ...getTableProps(), css: ReactTableStyle, style: styles.table(enableVirtualScroll), children: [_jsx(ReactTableHeader, { headerGroups: headerGroups }), _jsx("tbody", { ...getTableBodyProps(), children: rowsData.map((row, index) => {
                                    prepareRow(row);
                                    prepareRowSpan(rows, enableVirtualScroll ? index + indexBoundary.start : index, rowSpanHeaders, groupKey);
                                    return (_jsx(ReactTableRow, { row: row, ...row.getRowProps(), onContextMenu: (e) => contextMenuHandler(e, row), onClick: highlightActiveRow ? clickHandler : onClick, highlightedSource: highlightedSource, isRowActive: rowIndex === index }, row.key));
                                }) })] }), _jsx(ContextMenu, { ref: contextRef, context: context })] }), enableVirtualScroll && (_jsxs("p", { style: {
                    ...counterStyle,
                    opacity: !isCounterVisible ? '0' : '1',
                    transition: 'all 0.5s',
                    visibility: !isCounterVisible ? 'hidden' : 'visible',
                }, children: [index + 1, " / ", total] }))] }));
});
function ReactTable(props) {
    const { data, approxItemHeight = 40, groupKey } = props;
    const containerRef = useRef(null);
    const [mRef, { height }] = useMeasure();
    const [tableVirtualConfig, setTableVirtualConfig] = useState({
        scrollHeight: 0,
        numberOfVisibleRows: 0,
        index: { start: 0, end: 0 },
    });
    useEffect(() => {
        if (containerRef.current) {
            const { scrollHeight } = containerRef.current;
            const header = containerRef.current.querySelectorAll('thead');
            const numberOfVisibleRows = Math.ceil((height - header[0].clientHeight) / approxItemHeight);
            setTableVirtualConfig((prev) => ({
                ...prev,
                scrollHeight,
                numberOfVisibleRows: numberOfVisibleRows - 1,
                index: { start: 0, end: numberOfVisibleRows },
            }));
        }
    }, [approxItemHeight, height]);
    function lookForGroupIndex(currentIndex, side) {
        const currentItem = data[currentIndex];
        if (currentItem.index && groupKey) {
            switch (side) {
                case -1: {
                    let index = currentIndex - 1;
                    while (index > 0) {
                        if (data[index][groupKey] !== currentItem[groupKey]) {
                            return index + 1;
                        }
                        index--;
                    }
                    return currentIndex;
                }
                case 1: {
                    let index = currentIndex + 1;
                    while (index < data.length) {
                        if (data[index][groupKey] !== currentItem[groupKey]) {
                            return index - 1;
                        }
                        index++;
                    }
                    return currentIndex;
                }
                default:
                    return currentIndex;
            }
        }
        return currentIndex;
    }
    function findStartIndex(index, numberOfVisibleRows) {
        const newIndex = index - numberOfVisibleRows;
        const currentIndx = newIndex >= data.length ? newIndex : index;
        // return currentIndx;
        // Look for the first index of the group
        return lookForGroupIndex(currentIndx, -1);
    }
    function findEndIndex(index, numberOfVisibleRows) {
        const newIndex = index + numberOfVisibleRows;
        const currentIndx = newIndex >= data.length ? data.length - 1 : newIndex;
        // return currentIndx;
        // Look for the last index of the group
        return lookForGroupIndex(currentIndx, 1);
    }
    function scrollHandler() {
        if (containerRef.current && tableVirtualConfig) {
            const { scrollTop } = containerRef.current;
            const { numberOfVisibleRows, index } = tableVirtualConfig;
            const currentIndx = Math.ceil(scrollTop / approxItemHeight);
            const start = findStartIndex(currentIndx, numberOfVisibleRows);
            if (currentIndx !== index.start) {
                const end = findEndIndex(currentIndx, numberOfVisibleRows);
                setTableVirtualConfig({
                    ...tableVirtualConfig,
                    index: { start, end: end + 1 },
                });
            }
        }
    }
    return (_jsx(ReactTableProvider, { value: tableVirtualConfig, children: _jsx("div", { ref: mRef, style: {
                position: 'relative',
                height: '100%',
            }, children: _jsx(ReactTableInner, { onScroll: scrollHandler, ref: containerRef, ...{ ...props } }) }) }));
}
export default memo(ReactTable);
